import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useRouteMatch } from "react-router-dom";
import usePrevious from './usePrevious'

import LoginScreen from './LoginScreen'
import ProfilePhoto from './ProfilePhoto'
import DeleteUser from './DeleteUser'
import { FirebaseContext } from './Firebase'
import 'firebase/auth'
import 'firebase/functions'
import List from './List'
import MetaTags  from "./MetaTags"
import './Loading.css';
import { Lock } from '@styled-icons/feather/Lock'
import { Layout } from "../common"
import TrackCard from "./../tracks/TrackCard"
import TelemetryImports from "../reports/TelemetryImports"

const useQueryRedirect = () => {
    const query = new URLSearchParams(useLocation().search)
    const redirect = query.get("redirect")
    if (!redirect || !redirect.startsWith("/")) return null
    try {
        const destination = new URL(redirect, window.location.origin)
        return destination.origin === window.location.origin
            ? `${destination.pathname}${destination.search}${destination.hash}`
            : null
    } catch (error) {
        return null
    }
}

async function fetchTypesenseFirstResult (filter_by) {
    const response = await fetch(
        `${process.env.GATSBY_TYPESENSE_PROTOCOL}://${
            process.env.GATSBY_TYPESENSE_HOST
        }${
            process.env.GATSBY_TYPESENSE_PORT == "443"
                ? ""
                : `:${process.env.GATSBY_TYPESENSE_PORT}`
        }/collections/tracks/documents/search?q=*&filter_by=${filter_by}`,
        {
            headers: {
                "X-TYPESENSE-API-KEY": `${process.env.GATSBY_TYPESENSE_READ_API}`,
            },
        }
    )
    const data = await response.json()
    return data?.hits ? data?.hits[0]?.document : null
} 

const FavoritesWrapper = ({ children }) => (
    <div className="bg-gray-100 w-screen ml-[-50vw] left-1/2 relative">
        <div className="max-w-screen-lg mx-auto p-6 md:p-10">
            <h2 className="text-3xl">Favorite Tracks</h2>
            <div className="grid divide-y md:divide-y-0 md:grid-cols-2 overflow-auto h-auto md:gap-10">
                {children}
            </div>
        </div>
    </div>
)

const Favorites = ({ favorites }) => {
    if (!favorites || favorites.length === 0) return <></>
    
    const [favoriteTracks, setFavoriteTracks] = useState([])

    useEffect(() => {
        const readTrackCardData = async () => {
            const trackCardData = await Promise.all(
                favorites.map(async ({trackId}) => {
                    const track = await fetchTypesenseFirstResult(
                        `track_id:=${trackId}`
                    )
                    return {...track}
                })
            )
            setFavoriteTracks(trackCardData)
        }
        readTrackCardData()
    }, [favorites])

    if (!favoriteTracks)
        return (
            <FavoritesWrapper>
                {favorites.map((f) => (
                    <TrackCard key={f.trackId} />
                ))}
            </FavoritesWrapper>
        )
    
    return (
        <FavoritesWrapper>
            {favoriteTracks.map((f) => (
                <TrackCard key={f.trackId} {...f} />
            ))}
        </FavoritesWrapper>
    )
}

export default () => {
    const auth = useContext(FirebaseContext)
    const { user, userPublic, loadUserPublicDetailsFromUsername } = auth
    const firebase = auth.app
    const [portalLoading, setPortalLoading] = useState(false)
    const [portalError, setPortalError] = useState(null)
    const match = useRouteMatch("/p/:username")
    const prevMatch = usePrevious(match)
    const [publicUser, setPublicUser] = useState(null)
    const redirect = useQueryRedirect()

    const loadByUsername = async username => {
        setPublicUser(true)
        setPublicUser(await loadUserPublicDetailsFromUsername(username))
    } 

    useEffect(() => {
        if(!match || (match && match.params.username
            && prevMatch && prevMatch.params.username
            && prevMatch.params.username == match.params.username)) {
              return
          }

          loadByUsername(match.params.username)
      }, [match])

    useEffect(() => {
        const associationPending = typeof window !== 'undefined'
            && sessionStorage.getItem('openRacerAccountAssociationPending') === '1'
        if(redirect && user && !user.isAnonymous && !associationPending) {
            window.location = redirect
        }
    }, [redirect, user])

    if(user === null) return <div className="lds-dual-ring"></div>

    if(publicUser == "not found") return (
        <Layout>
            <div className="site-header-content">
                <h1 className="site-title">Driver Not Found</h1>
                <p>Oops, we couldn't find this profile.</p>
            </div>
        </Layout>
    )

    if(publicUser) return (
        <Layout>
            <MetaTags 
                title={`${publicUser.displayName}'s Driver Profile - Racing Lines and Favorite Tracks`}
                description={`See ${publicUser.displayName}'s racing lines and favorite tracks.`}
                image={publicUser.photoURL}
                url={`/p/${publicUser.username}`} />
            <div className="site-header-content">
                        {publicUser.photoURL && <ProfilePhoto url={publicUser.photoURL} width="200" height="200" />}
                        {publicUser.displayName && <h1 className="site-title">{publicUser.displayName}</h1>}
            </div>
            <main id="site-main" className="site-main outer" role="main">
                <div className="inner">
                {publicUser.uid &&
                    <>
                    <Favorites favorites={publicUser?.favorites}/>
                    <div>
                        <h2>Racing Lines</h2>
                         <List
                            filterUser={publicUser.uid}   
                            limit="30" 
                            publicLinesOnly={true} 
                            closedLinesOnly={true} 
                            oneRacingLinePerTrack={true} 
                            showProfilePhoto={false}
                            showLike={true}
                            wrapperClass="post-feed horizontal-feed lines"/>
                    </div>
                    </>
                }
                </div>
            </main>
            
        </Layout>
    )

    return (
        <>
            {!user || user.isAnonymous
                ?   <LoginScreen />
                :   <Layout><div className="site-header-content">
                        {userPublic && userPublic.photoURL &&<ProfilePhoto url={userPublic.photoURL} email={user.email} width="200" height="200" />}
                        {userPublic && userPublic.displayName && <h1 className="site-title">{userPublic.displayName}</h1>}
                        {userPublic && userPublic.username && <a href={`/p/${userPublic.username}`}>View your public profile</a>}
                        <p>{user.email} <Lock className="h-4"/></p>
                        <button onClick={auth.signout}>Sign-out</button>
                        <div className="mt-4">
                          <button
                            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-200 transition font-semibold"
                            disabled={portalLoading}
                            onClick={async () => {
                              setPortalError(null)
                              setPortalLoading(true)
                              try {
                                const fn = firebase.functions && firebase.functions()
                                if (!fn || typeof fn.httpsCallable !== 'function') {
                                  throw new Error('Billing service unavailable')
                                }
                                const createPortalLink = fn.httpsCallable('ext-firestore-stripe-payments-createPortalLink')
                                const { data } = await createPortalLink({ returnUrl: window.location.origin + '/profile' })
                                if (data?.url) {
                                  window.location.assign(data.url)
                                } else {
                                  throw new Error('Failed to get portal URL')
                                }
                              } catch (e) {
                                console.error('Portal error:', e)
                                setPortalError(e.message || 'Unable to open customer portal')
                              } finally {
                                setPortalLoading(false)
                              }
                            }}
                          >
                            {portalLoading ? 'Opening Portal…' : 'Manage Subscription'}
                          </button>
                          {portalError && (
                            <p className="text-sm text-red-600 mt-2">{portalError}</p>
                          )}
                        </div>
                        <div className="mt-4">
                          <DeleteUser signout={auth.signout} />
                        </div>
                    </div>
                    <main id="site-main" className="site-main outer" role="main">
                        <div className="inner">
                        {user &&
                            <>
                            <Favorites favorites={userPublic?.favorites}/>
                            <TelemetryImports />
                            <div>
                                <h2>Racing Lines</h2>
                                <List
                                    filterUser={user.uid}   
                                    limit="30" 
                                    publicLinesOnly={false} 
                                    closedLinesOnly={false} 
                                    oneRacingLinePerTrack={false} 
                                    showProfilePhoto={false}
                                    wrapperClass="post-feed horizontal-feed lines"/>
                            </div>
                            </>
                        }
                        </div>
                    </main>
                    </Layout>
            }
           
            
        </>
    )
}